import React, { useState, useCallback, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import LanguageStringResolver from '../../fields/language/LanguageStringResolver';
import UploadPanel from '../../upload/UploadPanel';
import ConfirmationModal from '../../modals/ConfirmationModal';
import Logger, { LOG_LEVELS } from '../../../utils/logger';
import { parseLocalListingExcel } from '../../../utils/localListingExcelParser';
import { MAX_UPLOAD_FILE_SIZE_MB, ALLOWED_FILE_EXTENSIONS, LOCAL_LISTING_IMPORT_TEMPLATE_FILENAME } from '../../../utils/localListingImportConfig';
import ReadOnlyWrapper from '../../authorization/ReadOnlyWrapper';

const log = new Logger('LOCAL_LISTING_VAT_IMPORT_SECTION', LOG_LEVELS.DEBUG);

/**
 * LocalListingVATImportSection - Component for importing VAT Number sales transactions
 * via Excel template upload in Local Listing Open Returns Details page.
 */
const LocalListingVATImportSection = ({ onDataImported }) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Handles file upload request - shows confirmation modal
   */
  const handleUploadRequest = useCallback((filesToBeSent) => {
    log.debug('handleUploadRequest: Files received', filesToBeSent);
    setUploadError(null);

    if (!filesToBeSent || filesToBeSent.length === 0) {
      return;
    }

    setPendingFile(filesToBeSent[0]);
    setShowConfirmModal(true);
  }, []);

  /**
   * Processes the file after user confirms
   */
  const processFile = useCallback(async () => {
    if (!pendingFile || !isMountedRef.current) return;

    setIsProcessing(true);
    setUploadError(null);

    try {
      log.debug('processFile: Processing file', pendingFile.name);
      const result = await parseLocalListingExcel(pendingFile);

      if (!isMountedRef.current) return;

      if (!result.success) {
        setUploadError(result.errors.join('\n'));
        log.error('processFile: Parsing errors', result.errors);
        return;
      }

      log.debug('processFile: Parsed lines', result.lines.length);
      onDataImported(result.lines);
    } catch (error) {
      log.error('processFile: Error', error);
      if (isMountedRef.current) {
        setUploadError(`Failed to process file: ${error.message}`);
      }
    } finally {
      if (isMountedRef.current) {
        setIsProcessing(false);
        setPendingFile(null);
      }
    }
  }, [pendingFile, onDataImported]);

  /**
   * Handles confirmation modal "Yes" button click.
   * Closes the modal and triggers file processing.
   */
  const handleConfirmYes = useCallback(() => {
    setShowConfirmModal(false);
    processFile();
  }, [processFile]);

  /**
   * Handles confirmation modal "No/Cancel" button click.
   * Closes the modal and clears the pending file without processing.
   */
  const handleConfirmNo = useCallback(() => {
    setShowConfirmModal(false);
    setPendingFile(null);
  }, []);

  /**
   * Handles clearing of uploaded files.
   * Resets pending file and any upload errors.
   */
  const handleClearFiles = useCallback(() => {
    setPendingFile(null);
    setUploadError(null);
  }, []);

  return (
    <div className="vat-import-section mt30">
      <h3 className="mb15">
        <LanguageStringResolver string="localListingFilingValues_ImportVATTitle" />
      </h3>

      {/* Information Messages */}
      <div className="vat-import-messages mb20">
        <s-box>
          <ul className="list-disc pl20">
            <li className="mb10">
              <LanguageStringResolver string="localListingFilingValues_ImportVATInstruction1" />
            </li>
            <li className="mb10">
              <LanguageStringResolver string="localListingFilingValues_ImportVATInstruction2" />
            </li>
            <li>
              <LanguageStringResolver string="localListingFilingValues_ImportVATInstruction3" />
            </li>
          </ul>
        </s-box>
      </div>

      <s-row>
        {/* Download Template Section */}
        <s-col span="6">
          <s-box>
            <h4 className="mb10">
              <LanguageStringResolver string="localListingFilingValues_DownloadTemplateTitle" />
            </h4>
            <p className="mb15">
              <LanguageStringResolver string="localListingFilingValues_DownloadTemplateDescription" />
            </p>
            <a 
              className="button secondary mb10" 
              href={`/${LOCAL_LISTING_IMPORT_TEMPLATE_FILENAME}`} 
              download={LOCAL_LISTING_IMPORT_TEMPLATE_FILENAME}
              data-test="download-template-btn"
            >
              <LanguageStringResolver string="localListingFilingValues_DownloadTemplateBtn" />
            </a>
          </s-box>
        </s-col>

        {/* Upload Section */}
        <s-col span="6">
          <label className="mb10">
            <strong style={{ color: 'red' }}>* </strong>
            <LanguageStringResolver string="localListingFilingValues_UploadDataLabel" />
          </label>
          <div className="vat-upload-panel-wrapper">
            <ReadOnlyWrapper>
              <UploadPanel
                uploadLimit={1}
                acceptTypes={ALLOWED_FILE_EXTENSIONS}
                onUploadRequest={handleUploadRequest}
                maxSizeInKB={MAX_UPLOAD_FILE_SIZE_MB * 1000}
                onClearFiles={handleClearFiles}
                disabled={isProcessing}
              />
            </ReadOnlyWrapper>
          </div>

          {uploadError && (
            <div className="mt10" style={{ color: 'red' }} data-test="upload-error">
              {uploadError}
            </div>
          )}

          {isProcessing && (
            <div className="processing-indicator mt10" data-test="processing-indicator">
              <LanguageStringResolver string="localListingFilingValues_Processing" />
            </div>
          )}
        </s-col>
      </s-row>

      <ConfirmationModal
        isModalPopupOpen={showConfirmModal}
        modelTitle="localListingFilingValues_ConfirmImportTitle"
        modelMessage="localListingFilingValues_ConfirmImportMessage"
        onYesClickHandler={handleConfirmYes}
        onCancelClickHandler={handleConfirmNo}
        dataTestId="local-listing-import-confirm"
      />
    </div>
  );
};


const mapStateToProps = (state) => ({
  languages: state.languages || {}
});

export default connect(mapStateToProps)(LocalListingVATImportSection);
